import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColumnFiltersState } from '@tanstack/react-table';
import type { AxiosError } from 'axios';
import { Inbox, RefreshCw, RotateCcw, Sparkles, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArticlesFilter, ArticleType } from './KnowledgeManager.dto';
import { AddContentCards } from './components/AddContentCards';
import { SyncedSitesPanel } from './components/SyncedSites.panel';
import { createColumns } from './components/columns';
import { DataTable } from './components/data-table';
import { httpClient } from '@/Config/api/httpClient.config';
import {
  ArticleBulkAutoRefreshInputDto,
  AutoTagArticleInput,
  AutoTagArticleResponseDto,
  BulkArticleRefreshInputDto,
  BulkAutoTagInputDto,
  BulkAutoTagResponseDto,
  applyArticleTagsToCachedLists,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { AccountDto } from '@/Domain/api/User/dto/Account.dto';
import { ArticleLimitModal } from '@/Domain/components/KnowledgeBase/ArticleLimitModal/ArticleLimit.modal';
import { AutoRefreshConfirmDialog } from '@/Domain/components/KnowledgeBase/AutoRefresh/AutoRefreshConfirmDialog';
import { AutoTagArticlesDialog } from '@/Domain/components/KnowledgeBase/AutoTagArticles/AutoTagArticles.dialog';
import { DeleteArticlesDialog } from '@/Domain/components/KnowledgeBase/DeleteArticles/DeleteArticles.dialog';
import { IntercomArticlesModal } from '@/Domain/components/KnowledgeBase/ExternalApps/IntercomArticles.modal';
import { ShopifyConnectModal } from '@/Domain/components/KnowledgeBase/ExternalApps/ShopifyConnect.modal';
import { ParagraphsSheet } from '@/Domain/components/KnowledgeBase/ParagraphsSheet/Paragraphs.sheet';
import { RefreshArticlesDialog } from '@/Domain/components/KnowledgeBase/RefreshArticles/RefreshArticles.dialog';
import { RefreshArticlesLimitDialog } from '@/Domain/components/KnowledgeBase/RefreshArticles/RefreshArticlesLimit.dialog';
import { UploadArticlesModal } from '@/Domain/components/KnowledgeBase/UploadFileModal/UploadArticles.modal';
import { UploadUrlModal } from '@/Domain/components/KnowledgeBase/UploadUrlModal/UploadUrl.modal';
import { UploadYoutubeModal } from '@/Domain/components/KnowledgeBase/UploadYoutubeModal/UploadYoutube.modal';
import { refreshFailureReasonKey } from '@/Domain/components/KnowledgeBase/articleRefreshFailure';
import {
  type Article,
  type ArticleInput,
  type ArticlesList,
} from '@/Domain/components/KnowledgeBaseArticles/types';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { useSocket } from '@/Domain/components/SocketProvider';
import { ArticleStateChange, ArticleStateEnum } from '@/Domain/components/TopBar/types';
import { PrivilegesEnum } from '@/Domain/enum/Privileges.enum';
import { useArticleAutoRefresh } from '@/Domain/hooks/KnowledgeBase/useArticleAutoRefresh.hook';
import { useAutoRefreshAvailability } from '@/Domain/hooks/KnowledgeBase/useAutoRefreshAvailability.hook';
import { useFetchParagraphs } from '@/Domain/hooks/KnowledgeBase/useFetchParagraphs.hook';
import { useRefreshArticle } from '@/Domain/hooks/KnowledgeBase/useRefreshArticle.hook';
import { useUploadArticles } from '@/Domain/hooks/KnowledgeBase/useUploadArticles.hook';
import { useThrottledRefetch } from '@/Domain/hooks/Shared/useThrottledRefetch.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import {
  markAutoRefreshWarningShown,
  shouldShowAutoRefreshWarning,
} from '@/Domain/utils/autoRefreshWarning';

// 100 matches the trial plan's articles_limit so free users can see the
// full KB on a single page, which clears the "clean up to free space"
// loop in one bulk-select. Paid plans (500 to 10k) still paginate but in
// fewer hops.
export const ARTICLES_PAGE_LIMIT = 100;

const REFRESHABLE_ARTICLE_TYPES: ArticleType[] = [ArticleType.URL];

type SelectedArticleMeta = {
  id: string;
  type: string;
  // Snapshot of the row's tag count when it was selected. Used to compute
  // bulk-auto-tag eligibility (zero tags) without re-fetching when the
  // selection spans paginated pages and the row is no longer in view.
  tagCount: number;
};

const normalizeRowSelection = (selection: Record<string, boolean>): Record<string, boolean> =>
  Object.fromEntries(Object.entries(selection).filter(([, isSelected]) => isSelected));

const isRefreshableArticle = (articleType: string) =>
  REFRESHABLE_ARTICLE_TYPES.includes(articleType as ArticleType);

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

export const KnowledgeManagerPage = () => {
  const { t } = useTranslation('knowledge_base');
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const { toggleModal, closeModal } = useModalStore();
  const account = queryClient.getQueryData<AccountDto>(['account']);
  const navigate = useNavigate();
  const { handleUploadFiles } = useUploadArticles();
  const { refreshArticle } = useRefreshArticle();
  const { available: autoRefreshAvailable } = useAutoRefreshAvailability();
  const { toggle: toggleAutoRefresh, togglingIds: togglingAutoRefreshIds } =
    useArticleAutoRefresh();
  const refreshAllMutation = useMutation({
    mutationKey: [QueryKey.knowledgeBase.articles.refreshAll],
  });
  const autoRefreshBulkMutation = useMutation<unknown, AxiosError, ArticleBulkAutoRefreshInputDto>({
    mutationKey: [QueryKey.knowledgeBase.articles.autoRefreshBulk],
  });
  const { mutate: autoTagArticle } = useMutation<
    AutoTagArticleResponseDto,
    AxiosError,
    AutoTagArticleInput
  >({
    mutationKey: [QueryKey.knowledgeBase.articles.autoTag],
  });
  const autoTagBulkMutation = useMutation<
    BulkAutoTagResponseDto,
    AxiosError,
    BulkAutoTagInputDto | undefined
  >({
    mutationKey: [QueryKey.knowledgeBase.articles.autoTagBulk],
  });
  const [autoTaggingArticleIds, setAutoTaggingArticleIds] = useState<Set<string>>(new Set());
  const [refreshingArticleIds, setRefreshingArticleIds] = useState<Set<string>>(new Set());
  const refreshingArticleIdsRef = useRef<Set<string>>(refreshingArticleIds);

  // Keep ref in sync with state
  useEffect(() => {
    refreshingArticleIdsRef.current = refreshingArticleIds;
  }, [refreshingArticleIds]);

  // Throttle KB list refetches during a burst of SCRAPING_PING events.
  // Firecrawl can fire 100 crawl.page webhooks in <5s, and the backend emits
  // one SCRAPING_PING per page. Without throttling we'd hammer the list
  // endpoint; with invalidateQueries alone (combined with global
  // staleTime: Infinity) refetches can be skipped, so the hook uses
  // refetchQueries explicitly. dropInactivePages drops prefetched pages so
  // navigating from page 1 to page 2 mid-scrape sees the up-to-date count
  // instead of stale prefetched data from when fewer articles existed.
  const scheduleListRefetch = useThrottledRefetch([QueryKey.knowledgeBase.articlesInfinite]);

  useEffect(() => {
    const handleArticleStateChanged = (data: ArticleStateChange) => {
      // Refetch failures because their embed state deliberately remains unchanged.
      if (
        data.state === ArticleStateEnum.DONE ||
        data.state === ArticleStateEnum.SCRAPING_PING ||
        data.state === ArticleStateEnum.REFRESH_FAILED
      ) {
        scheduleListRefetch();
      }

      // Note: We do NOT add articles to refreshingArticleIds on SCRAPING event.
      // Articles are only added when user explicitly clicks refresh from the table (handleRefreshArticle).
      // This prevents showing duplicate toasts when refresh is triggered from ArticleSheet.

      // Clear refreshing state and show toast when article refresh completes
      if (
        data.state === ArticleStateEnum.DONE ||
        data.state === ArticleStateEnum.ERROR ||
        data.state === ArticleStateEnum.REFRESH_FAILED ||
        data.state === ArticleStateEnum.META_ARTICLE_DELETED
      ) {
        // Use ref to check if article was being refreshed from this page (avoids stale closure)
        const wasRefreshingFromTable = refreshingArticleIdsRef.current.has(String(data.article_id));
        setRefreshingArticleIds((prev) => {
          const next = new Set(prev);
          next.delete(String(data.article_id));
          return next;
        });

        // Show toast only if this article was refreshed from the table (not from ArticleSheet)
        if (wasRefreshingFromTable && data.state === ArticleStateEnum.DONE) {
          toast.success(t('knowledge_base:article.toast.refresh_completed'));
        } else if (
          wasRefreshingFromTable &&
          (data.state === ArticleStateEnum.ERROR || data.state === ArticleStateEnum.REFRESH_FAILED)
        ) {
          toast.error(
            t('knowledge_base:article.toast.refresh_failed_with_reason', {
              reason: t(`knowledge_base:${refreshFailureReasonKey(data.error_message)}`),
            }),
          );
        }
      }

      // Tag-column spinner: drives both the single-article path (clicked from
      // the row dropdown or the sheet) and the batch path (post-scrape fan-out),
      // since both go through apply_auto_tags which emits these events.
      if (data.state === ArticleStateEnum.AUTO_TAGGING) {
        setAutoTaggingArticleIds((prev) => {
          const next = new Set(prev);
          next.add(String(data.article_id));
          return next;
        });
      } else if (data.state === ArticleStateEnum.AUTO_TAGGED) {
        const articleId = String(data.article_id);
        // Paint before dropping the spinner so the row goes spinner -> chips in
        // one commit, with no em-dash flash while a refetch is in flight.
        if (data.tags?.length) {
          applyArticleTagsToCachedLists(articleId, data.tags);
        }
        setAutoTaggingArticleIds((prev) => {
          const next = new Set(prev);
          next.delete(articleId);
          return next;
        });
        // Still reconcile: skip/failure events carry no tags, and some rows
        // aren't cached.
        scheduleListRefetch();
      }
    };

    socket.on(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);

    return () => {
      socket.off(SocketKey.knowledgeBase.articleStateChanged, handleArticleStateChanged);
    };
  }, [socket, queryClient, t, scheduleListRefetch]);

  // Mirror the page-header pill: refetch the article list whenever the
  // backend broadcasts knowledge_base_updated. SCRAPING_PING covers the
  // webhook-mode happy path, but sync-polling fallback (no Firecrawl webhook
  // configured) emits no SCRAPING_PINGs — only knowledge_base_updated +
  // META_ARTICLE_DELETED at cleanup end. Keying off knowledge_base_updated
  // makes the list update in lockstep with the pill in both modes.
  useEffect(() => {
    const handleKbUpdated = () => {
      scheduleListRefetch();
    };
    socket.on(SocketKey.knowledgeBase.knowledgeBaseUpdated, handleKbUpdated);
    return () => {
      socket.off(SocketKey.knowledgeBase.knowledgeBaseUpdated, handleKbUpdated);
    };
  }, [socket, scheduleListRefetch]);

  useEffect(() => {
    const handleEmbedStateChanged = () => {
      scheduleListRefetch();
    };
    socket.on(SocketKey.knowledgeBase.articleEmbedStateChanged, handleEmbedStateChanged);
    return () => {
      socket.off(SocketKey.knowledgeBase.articleEmbedStateChanged, handleEmbedStateChanged);
    };
  }, [socket, scheduleListRefetch]);

  const [columnFiltersState, setColumnFiltersState] = useState<ColumnFiltersState>([]);
  const [globalFilterState, setGlobalFilterState] = useState('');

  const filters = useMemo<ArticlesFilter>(() => {
    const result: ArticlesFilter = {};

    for (const f of columnFiltersState) {
      switch (f.id) {
        case 'type':
          result.types = f.value as ArticleType[];
          break;
        case 'tags':
          result.tags = f.value as string[];
          break;
        case 'url':
          result.url = f.value as string;
          break;
        case 'title':
          result.title = f.value as string;
          break;
      }
    }

    if (globalFilterState) {
      result.query = globalFilterState;
    }

    return result;
  }, [columnFiltersState, globalFilterState]);

  const debouncedFilters = useDebouncedValue(filters, 500);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const offset = currentPageIndex * ARTICLES_PAGE_LIMIT;

  const { data, isLoading, isFetching } = useQuery<ArticlesList>({
    queryKey: [
      QueryKey.knowledgeBase.articlesInfinite,
      { offset, limit: ARTICLES_PAGE_LIMIT, filters: debouncedFilters },
    ],
    enabled: account?.privileges.includes(PrivilegesEnum.EXTERNAL_SOURCES),
    placeholderData: keepPreviousData,
  });

  // Independent of the user's filters: do any URL articles exist anywhere
  // in the KB? Drives the "Refresh all URLs" button's disabled state.
  // Defaults to disabled until the probe confirms at least one URL article.
  const { data: urlArticlesProbe } = useQuery<ArticlesList>({
    queryKey: [
      QueryKey.knowledgeBase.articlesInfinite,
      { offset: 0, limit: 1, filters: { types: [ArticleType.URL] } },
    ],
    enabled: account?.privileges.includes(PrivilegesEnum.EXTERNAL_SOURCES),
  });
  const hasAnyUrlArticles = (urlArticlesProbe?.count ?? 0) > 0;

  // Drives whether the synthetic "Single Paragraphs" row is shown in the table.
  // Shares the sheet's infinite-query cache so create/remove optimistic updates
  // toggle the row live; keys off item presence since those updates don't touch count.
  const { data: paragraphsData } = useFetchParagraphs(
    '',
    account?.privileges.includes(PrivilegesEnum.EXTERNAL_SOURCES) ?? false,
  );
  const hasAnyParagraphs = paragraphsData?.pages?.some((page) => page.items.length > 0) ?? false;

  const currentPageArticles = data?.items ?? [];
  const totalItems = data?.count ?? 0;
  const isEmpty = totalItems === 0;

  useEffect(() => {
    if (!data || isLoading || isFetching) return;

    if (totalItems === 0) return;
    const pageCount = Math.ceil(totalItems / ARTICLES_PAGE_LIMIT);

    const firstPage = 0;
    const lastPage = pageCount - 1;
    const prevPage = currentPageIndex - 1;
    const nextPage = currentPageIndex + 1;

    const pagesToPrefetch = new Set<number>();
    pagesToPrefetch.add(firstPage);
    pagesToPrefetch.add(lastPage);
    if (prevPage >= 0) pagesToPrefetch.add(prevPage);
    if (nextPage < pageCount) pagesToPrefetch.add(nextPage);

    pagesToPrefetch.forEach((pageIndex) => {
      if (pageIndex === currentPageIndex) return;

      const prefetchOffset = pageIndex * ARTICLES_PAGE_LIMIT;

      queryClient.prefetchQuery({
        queryKey: [
          QueryKey.knowledgeBase.articlesInfinite,
          { offset: prefetchOffset, limit: ARTICLES_PAGE_LIMIT, filters: debouncedFilters },
        ],
      });
    });
  }, [data, currentPageIndex, queryClient, debouncedFilters, isLoading, isFetching]);

  const deleteArticlesMutation = useMutation<unknown, AxiosError<ArticleInput>, string | string[]>({
    mutationKey: ['article-delete'],
    mutationFn: async (ids) => {
      const targetIds = Array.isArray(ids) ? ids : [ids];
      await httpClient.delete('/v1/api/knowledge_base/articles/', { data: targetIds });
    },
    onSuccess: (_, ids) => {
      const targetIds = Array.isArray(ids) ? ids : [ids];

      queryClient.invalidateQueries({
        queryKey: [QueryKey.knowledgeBase.articlesInfinite],
      });
      queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });

      if (targetIds.length === 1) {
        toast.success(t('toast.delete_success_single'));
      } else {
        toast.success(t('toast.delete_success_plural', { count: targetIds.length }));
      }
      handleClearSelection();
    },
    onError: (_, ids) => {
      const targetIds = Array.isArray(ids) ? ids : [ids];
      const isBulk = targetIds.length > 1;
      toast.error(isBulk ? t('toast.delete_error_plural') : t('toast.delete_error_single'));
    },
    onSettled: () => {
      closeModal();
    },
  });

  const handleRowClick = useCallback(
    (article: Article): void => {
      navigate(`articles/${article.id}`, { viewTransition: true });
    },
    [navigate],
  );

  const handleSingleParagraphClick = useCallback(
    () => toggleModal(MODAL_NAMES.PARAGRAPH_SHEET),
    [toggleModal],
  );

  const handleDeleteArticle = useCallback(
    (article: Article): void => {
      toggleModal(MODAL_NAMES.DELETE_ARTICLES_DIALOG, {
        articleIds: [article.id],
        articleTitle: article.title,
      });
    },
    [toggleModal],
  );

  const handleEditArticle = useCallback(
    (articleId: string): void => {
      navigate(`articles/${articleId}`, { viewTransition: true });
    },
    [navigate],
  );

  const handleRefreshArticle = useCallback(
    (articleId: string): void => {
      setRefreshingArticleIds((prev) => new Set(prev).add(articleId));
      refreshArticle(Number(articleId));
    },
    [refreshArticle],
  );

  const handleUpload = (files: File[]) => {
    handleUploadFiles(files);
  };

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [selectedArticleCache, setSelectedArticleCache] = useState<
    Record<string, SelectedArticleMeta>
  >({});

  useEffect(() => {
    setSelectedArticleCache((prev) => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach((id) => {
        if (!selectedRows[id]) {
          delete next[id];
          changed = true;
        }
      });

      currentPageArticles.forEach((article) => {
        const id = String(article.id);
        if (!selectedRows[id]) {
          return;
        }

        const tagCount = article.tags?.length ?? 0;
        const cached = next[id];
        if (cached?.type !== article.type || cached?.tagCount !== tagCount) {
          next[id] = { id, type: article.type, tagCount };
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [currentPageArticles, selectedRows]);

  const selectedIds = useMemo(() => Object.keys(selectedRows), [selectedRows]);
  const selectedCount = selectedIds.length;

  const handleRowSelectionChange = useCallback((selection: Record<string, boolean>) => {
    setSelectedRows(normalizeRowSelection(selection));
  }, []);

  const handleSelectAll = () => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      currentPageArticles.forEach((article) => {
        next[String(article.id)] = true;
      });
      return normalizeRowSelection(next);
    });
  };

  const handlePageChange = (pageIndex: number) => {
    setCurrentPageIndex(pageIndex);
  };

  const handleClearSelection = () => {
    setSelectedRows((prev) => (Object.keys(prev).length ? {} : prev));
    setSelectedArticleCache((prev) => (Object.keys(prev).length ? {} : prev));
  };

  const [bulkAutoRefreshConfirmOpen, setBulkAutoRefreshConfirmOpen] = useState(false);

  const runBulkAutoRefreshEnable = () => {
    autoRefreshBulkMutation.mutate(
      {
        article_ids: selectedUrlArticleIds.map((id) => Number(id)),
        enabled: true,
      },
      { onSuccess: handleClearSelection },
    );
  };

  const openBulkDeleteDialog = () => {
    toggleModal(MODAL_NAMES.DELETE_ARTICLES_DIALOG, {
      articleIds: selectedIds,
    });
  };

  const selectedUrlArticleIds = useMemo(
    () =>
      selectedIds.filter((id) => {
        const article =
          selectedArticleCache[id] ??
          currentPageArticles.find((currentArticle) => String(currentArticle.id) === id);
        return article ? isRefreshableArticle(article.type) : false;
      }),
    [currentPageArticles, selectedArticleCache, selectedIds],
  );

  // Only untagged articles are eligible for bulk auto-tag (the backend
  // enforces this; we compute it here to disable the button, pass an
  // accurate articleIds payload, and surface a "skipping N" hint in the
  // confirmation dialog).
  const selectedUntaggedArticleIds = useMemo(
    () =>
      selectedIds.filter((id) => {
        const cached = selectedArticleCache[id];
        if (cached) return cached.tagCount === 0;
        const article = currentPageArticles.find(
          (currentArticle) => String(currentArticle.id) === id,
        );
        return article ? (article.tags?.length ?? 0) === 0 : false;
      }),
    [currentPageArticles, selectedArticleCache, selectedIds],
  );

  const openBulkRefreshDialog = () => {
    toggleModal(MODAL_NAMES.REFRESH_ARTICLES_DIALOG, {
      mode: 'selected',
      articleIds: selectedUrlArticleIds,
    });
  };

  const openBulkAutoTagDialog = () => {
    toggleModal(MODAL_NAMES.AUTO_TAG_ARTICLES_DIALOG, {
      articleIds: selectedUntaggedArticleIds,
      selectedCount,
    });
  };

  const openRefreshAllDialog = () => {
    toggleModal(MODAL_NAMES.REFRESH_ARTICLES_DIALOG, { mode: 'all' });
  };

  const handleAutoTagArticle = useCallback(
    (articleId: string) => {
      const id = Number(articleId);
      if (Number.isNaN(id)) return;
      setAutoTaggingArticleIds((prev) => {
        const next = new Set(prev);
        next.add(articleId);
        return next;
      });
      // Dropdown path: tag from whatever's saved in the DB — there's no open
      // form state here to pass as overrides.
      autoTagArticle(
        { articleId: id },
        {
          onSettled: () => {
            setAutoTaggingArticleIds((prev) => {
              const next = new Set(prev);
              next.delete(articleId);
              return next;
            });
          },
        },
      );
    },
    [autoTagArticle],
  );

  const columns = useMemo(
    () =>
      createColumns(
        t,
        handleEditArticle,
        handleDeleteArticle,
        handleRefreshArticle,
        refreshingArticleIds,
        handleAutoTagArticle,
        autoTaggingArticleIds,
        toggleAutoRefresh,
        autoRefreshAvailable,
        togglingAutoRefreshIds,
      ),
    [
      t,
      handleEditArticle,
      handleDeleteArticle,
      handleRefreshArticle,
      refreshingArticleIds,
      handleAutoTagArticle,
      autoTaggingArticleIds,
      toggleAutoRefresh,
      autoRefreshAvailable,
      togglingAutoRefreshIds,
    ],
  );

  if (!account?.privileges.includes(PrivilegesEnum.EXTERNAL_SOURCES)) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col space-y-6">
      <AddContentCards />

      <SyncedSitesPanel />

      <div className="flex flex-1 flex-col pt-2 mb-0">
        {isLoading ? (
          <div className="flex flex-1 justify-center items-center py-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={currentPageArticles}
            totalItems={totalItems}
            pageSize={ARTICLES_PAGE_LIMIT}
            onPageChange={handlePageChange}
            onRowClick={handleRowClick}
            rowSelection={selectedRows}
            onRowSelectionChange={handleRowSelectionChange}
            onSingleParagraphClick={handleSingleParagraphClick}
            hasSingleParagraphs={hasAnyParagraphs}
            emptyState={
              !isEmpty ? undefined : (
                <div className="my-12 flex flex-col items-center justify-center gap-3">
                  <Inbox className="h-8 w-8 text-foreground-subtle" />
                  <span className="text-sm font-semibold text-foreground">{t('empty.title')}</span>
                  <span className="mb-3 text-sm text-muted-foreground">
                    {t('empty.description')}
                  </span>
                </div>
              )
            }
            onRefreshAll={openRefreshAllDialog}
            refreshAllDisabled={!hasAnyUrlArticles}
            onColumnFiltersChangeExternal={(next) => {
              handleClearSelection();
              setCurrentPageIndex(0);
              setColumnFiltersState(next);
            }}
            onGlobalFilterChangeExternal={(next) => {
              handleClearSelection();
              setCurrentPageIndex(0);
              setGlobalFilterState(next);
            }}
            selectionToolbar={
              selectedCount > 0 ? (
                <div className="relative flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pr-4">
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-foreground">
                      {t('articles.list.select.count')}{' '}
                      <span className="text-primary-600">{selectedCount}</span>
                    </span>
                    <div className="h-4 w-px bg-border" />
                    <Button variant="link" size="sm" onClick={handleSelectAll} className="text-sm">
                      {t('articles.list.select.select_all', { count: currentPageArticles.length })}
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleClearSelection}
                      className="text-sm"
                    >
                      {t('articles.list.select.clear')}
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    {selectedUntaggedArticleIds.length > 0 && (
                      <Button
                        variant="primary_dark_light"
                        size="sm"
                        onClick={openBulkAutoTagDialog}
                        data-testid="bulk-auto-tag-button"
                        className="flex items-center gap-2"
                      >
                        <Sparkles className="h-4 w-4" />
                        {t('articles.list.select.auto_tag', {
                          count: selectedUntaggedArticleIds.length,
                        })}
                      </Button>
                    )}
                    {selectedUrlArticleIds.length > 0 && autoRefreshAvailable && (
                      <Button
                        variant="primary_dark_light"
                        size="sm"
                        disabled={autoRefreshBulkMutation.isPending}
                        onClick={() => {
                          if (shouldShowAutoRefreshWarning()) {
                            setBulkAutoRefreshConfirmOpen(true);
                          } else {
                            runBulkAutoRefreshEnable();
                          }
                        }}
                        data-testid="bulk-auto-refresh-button"
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className="h-4 w-4" />
                        {t('articles.list.select.auto_refresh_on', {
                          count: selectedUrlArticleIds.length,
                        })}
                      </Button>
                    )}
                    {selectedUrlArticleIds.length > 0 && (
                      <Button
                        variant="primary_dark_light"
                        size="sm"
                        onClick={openBulkRefreshDialog}
                        data-testid="bulk-refresh-button"
                        className="flex items-center gap-2"
                      >
                        <RotateCcw className="h-4 w-4" />
                        {t('articles.list.select.refresh_urls', {
                          count: selectedUrlArticleIds.length,
                        })}
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={openBulkDeleteDialog}
                      aria-label={t('articles.list.select.delete')}
                      className="flex items-center"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearSelection}
                    aria-label={t('articles.list.select.cancel')}
                    className="absolute -right-2 top-0 -m-1 cursor-pointer rounded-md p-1 text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground"
                  >
                    <X className="text-current size-4" />
                  </button>
                </div>
              ) : undefined
            }
          />
        )}
      </div>

      <ShopifyConnectModal />
      <IntercomArticlesModal />
      <ParagraphsSheet />
      <UploadArticlesModal maxFileSize={32} onUpload={handleUpload} />
      <ArticleLimitModal />
      <UploadUrlModal />
      <UploadYoutubeModal />

      <DeleteArticlesDialog
        isLoading={deleteArticlesMutation.isPending}
        onConfirm={(ids) => {
          if (!ids.length) {
            return;
          }
          deleteArticlesMutation.mutate(ids);
        }}
      />

      <RefreshArticlesDialog
        isLoading={refreshAllMutation.isPending}
        onConfirm={(params) => {
          const handleBatchRefreshError = (error: AxiosError | unknown) => {
            closeModal();
            if ((error as AxiosError).response?.status === 400) {
              toggleModal(MODAL_NAMES.REFRESH_ARTICLES_LIMIT_DIALOG);
            }
            // Non-400 toast is handled by queryClient defaults
          };

          const markArticlesAsRefreshing = (articleIds: string[]) => {
            if (articleIds.length === 0) {
              return;
            }
            setRefreshingArticleIds((prev) => {
              const next = new Set(prev);
              articleIds.forEach((id) => next.add(id));
              return next;
            });
          };

          if (params.mode === 'all') {
            const visibleUrlIds = currentPageArticles
              .filter((a) => isRefreshableArticle(a.type))
              .map((a) => String(a.id));
            refreshAllMutation.mutate(undefined, {
              onSuccess: () => {
                markArticlesAsRefreshing(visibleUrlIds);
                closeModal();
              },
              onError: handleBatchRefreshError,
            });
          } else {
            const payload: BulkArticleRefreshInputDto = {
              article_ids: params.articleIds.map((id) => Number(id)),
            };
            refreshAllMutation.mutate(payload, {
              onSuccess: () => {
                markArticlesAsRefreshing(params.articleIds);
                closeModal();
              },
              onError: handleBatchRefreshError,
            });
          }
        }}
      />

      <RefreshArticlesLimitDialog />

      <AutoRefreshConfirmDialog
        open={bulkAutoRefreshConfirmOpen}
        onOpenChange={setBulkAutoRefreshConfirmOpen}
        onConfirm={() => {
          markAutoRefreshWarningShown();
          setBulkAutoRefreshConfirmOpen(false);
          runBulkAutoRefreshEnable();
        }}
      />

      <AutoTagArticlesDialog
        isLoading={autoTagBulkMutation.isPending}
        onConfirm={(articleIds) => {
          if (!articleIds.length) return;
          // Optimistically mark rows as auto-tagging — backend will emit
          // AUTO_TAGGED per article once each batch task finishes, which
          // clears them again.
          setAutoTaggingArticleIds((prev) => {
            const next = new Set(prev);
            articleIds.forEach((id) => next.add(id));
            return next;
          });
          const clearOptimisticSpinner = () => {
            setAutoTaggingArticleIds((prev) => {
              const next = new Set(prev);
              articleIds.forEach((id) => next.delete(id));
              return next;
            });
          };
          autoTagBulkMutation.mutate(
            { article_ids: articleIds.map((id) => Number(id)) },
            {
              // No dispatch on error — clear the optimistic spinner.
              onError: clearOptimisticSpinner,
              onSuccess: (data) => {
                // Backend silently dropped some ids (race); AUTO_TAGGED won't
                // fire for those. Clear all and let AUTO_TAGGING re-add live ones.
                if (data.dispatched < articleIds.length) {
                  clearOptimisticSpinner();
                }
              },
              onSettled: () => {
                closeModal();
                handleClearSelection();
              },
            },
          );
        }}
      />
    </div>
  );
};
